var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { FocusOn, AutoFocusInside } from "react-focus-on";
import { classnames } from "../../utils";
import FloatingUI from "../../components-internal/FloatingUI";
var PopoverBackdrop = styled.div(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  position: fixed;\n  left: 0;\n  top: 0;\n  right: 0;\n  bottom: 0;\n"], ["\n  position: fixed;\n  left: 0;\n  top: 0;\n  right: 0;\n  bottom: 0;\n"])));
var PopoverCenterContainer = styled.div(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  position: fixed;\n  left: 0;\n  top: 0;\n  right: 0;\n  bottom: 0;\n\n  .popover-content {\n    position: fixed;\n    left: 50%;\n    top: 50%;\n    transform: translate(-50%, -50%);\n  }\n"], ["\n  position: fixed;\n  left: 0;\n  top: 0;\n  right: 0;\n  bottom: 0;\n\n  .popover-content {\n    position: fixed;\n    left: 50%;\n    top: 50%;\n    transform: translate(-50%, -50%);\n  }\n"])));
var PopoverRightContainer = styled.div(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  position: fixed;\n  left: 0;\n  top: 0;\n  right: 0;\n  bottom: 0;\n\n  .popover-content {\n    position: fixed;\n    right: 0;\n    top: 0;\n    bottom: 0;\n  }\n"], ["\n  position: fixed;\n  left: 0;\n  top: 0;\n  right: 0;\n  bottom: 0;\n\n  .popover-content {\n    position: fixed;\n    right: 0;\n    top: 0;\n    bottom: 0;\n  }\n"])));
var FillAutoFocusInside = styled(AutoFocusInside)(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n  width: 100%;\n  height: 100%;\n"], ["\n  width: 100%;\n  height: 100%;\n"])));
var Popover = function (_a) {
    var closeOnBackgroundClick = _a.closeOnBackgroundClick, closeOnEscape = _a.closeOnEscape, open = _a.isOpen, placement = _a.placement, backdropClassName = _a.backdropClassName, backdropStyle = _a.backdropStyle, anchorEl = _a.anchorEl, children = _a.children, requestClose = _a.onRequestClose, _b = _a.scrollLock, scrollLock = _b === void 0 ? true : _b, divProps = __rest(_a, ["closeOnBackgroundClick", "closeOnEscape", "isOpen", "placement", "backdropClassName", "backdropStyle", "anchorEl", "children", "onRequestClose", "scrollLock"]);
    var contentRef = React.useRef(null);
    var onBackdropClick = React.useCallback(function () {
        if (closeOnBackgroundClick && requestClose) {
            requestClose();
        }
    }, [closeOnBackgroundClick, requestClose]);
    var onEscape = React.useCallback(function () {
        if (closeOnEscape && requestClose) {
            requestClose();
        }
    }, [closeOnEscape, requestClose]);
    var Container = React.useMemo(function () {
        if (placement === "center-screen") {
            return PopoverCenterContainer;
        }
        else if (placement === "right-screen") {
            return PopoverRightContainer;
        }
        else if (anchorEl) {
            return function (_a) {
                var children = _a.children;
                return (React.createElement(FloatingUI, { isOpen: true, placement: placement, anchorEl: anchorEl, 
                    // Disable the portal, as we are placing this inside an element we have already portaled.
                    // In this case, we only want to use positioning logic.  This is safe as our portaled content
                    // takes up the full viewport.
                    portal: false }, children));
            };
        }
        else {
            if (open) {
                console.error('Popover element requires anchorEl to be specified if placement is not "center-screen"');
            }
            return null;
        }
    }, [placement, anchorEl]);
    if (!open || !Container) {
        return null;
    }
    // A fragment is needed here to add both components to the document body.
    //  This does not break our no-fragments rule, as the root component
    //  we return does not emit elements at the level the component is used at.
    var contentNode = (React.createElement(React.Fragment, null,
        React.createElement(PopoverBackdrop, { className: classnames(backdropClassName, "popover-backdrop"), style: backdropStyle }),
        React.createElement(Container, null,
            React.createElement(FocusOn
            // FIXME: Autofocus causes rogue scrolling and breaks the popover.
            //  This is because autofocus happens before popper kicks in,
            //  so the browser tries to scroll to the old position of the content
            //  before popper has a chance to relocate it.
            , { 
                // FIXME: Autofocus causes rogue scrolling and breaks the popover.
                //  This is because autofocus happens before popper kicks in,
                //  so the browser tries to scroll to the old position of the content
                //  before popper has a chance to relocate it.
                autoFocus: false, onClickOutside: onBackdropClick, onEscapeKey: onEscape, scrollLock: scrollLock },
                React.createElement("div", __assign({ ref: contentRef }, divProps, { className: classnames("popover-content", divProps.className) }),
                    React.createElement(FillAutoFocusInside, null, children))))));
    return createPortal(contentNode, document.body);
};
export default Popover;
var templateObject_1, templateObject_2, templateObject_3, templateObject_4;
//# sourceMappingURL=Popover.js.map